<template>
    <div class = "lottie">
        <div v-if="trigger == 'hover'" class = "w-full h-full" ref = "lottie" v-sr="{ beforeReveal: handleTrigger, distance: 0, opacity: 1 }" @mouseenter="playAnimation(1)" @mouseleave="playAnimation(-1)"></div>
        <div v-else-if="trigger == 'load'" class = "w-full h-full" ref = "lottie" v-sr="{ beforeReveal: handleTrigger, distance: 0, opacity: 1 }"></div>
        <div v-else-if="trigger == 'function'" class = "w-full h-full" ref = "lottie"></div>
    </div>
</template>
<script>
import lottie from 'lottie-web';

export default {
    name: "LottieAnimation",
    props: [
        "path",
        "trigger",
        "hovering",
        "fit",
        "loop"
    ],
    data() {
        return {
            lottie: null,
        }
    },
    created() {
        this._isLoaded = false;
        this._pendingSeekFrame = null;
    },
    emits: [ 'completed', 'playing' ],
    mounted () {
        if (!this.$refs.lottie) return;

        this.lottie = lottie.loadAnimation({
            wrapper: this.$refs.lottie,
            animType: 'svg',
            loop: typeof this.loop !== 'undefined' ? this.loop : false,
            autoplay: false,
            animationData: this.path,
            rendererSettings: {
                preserveAspectRatio: `xMidYMid ${this.fit == 'contain' ? 'meet' : 'slice'}` // or 'none' if you want to stretch
            }
        });
        
        // Defensive selector: check if SVG element is immediately created
        if (this.$refs.lottie) {
            const svg = this.$refs.lottie.querySelector('svg');
            if (svg) {
                svg.style.height = '100%';
                svg.style.width = '100%';
            }
        }

        // Event listener fallback: in case of asynchronous SVG insertion in dynamic modules
        this.lottie.addEventListener('DOMLoaded', () => {
            this._isLoaded = true;
            if (this.$refs.lottie) {
                const nestedSvg = this.$refs.lottie.querySelector('svg');
                if (nestedSvg) {
                    nestedSvg.style.height = '100%';
                    nestedSvg.style.width = '100%';
                }
            }
            
            // 🚀 Safari Vector Warmup: Force-render the first frame instantly to 
            // compile the SVG paths and prime the GPU layers while the intro video plays!
            if (this.lottie) {
                if (this._pendingSeekFrame) {
                    this.lottie.goToAndStop(this._pendingSeekFrame.value, this._pendingSeekFrame.isFrame);
                    this._pendingSeekFrame = null;
                } else {
                    this.lottie.goToAndStop(0, true);
                }
            }
        });

        this.lottie.addEventListener('complete', this.emitComplete);

        if (typeof ResizeObserver !== 'undefined' && this.$refs.lottie) {
            this._resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const { width, height } = entry.contentRect;
                    if (width > 0 && height > 0) {
                        if (this.lottie) {
                            this.lottie.resize();
                        }
                    }
                }
            });
            this._resizeObserver.observe(this.$refs.lottie);
        }
    },
    beforeUnmount() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
        if (this.lottie) {
            this.lottie.destroy();
            this.lottie = null;
        }
    },
    methods: {
        resize() {
            if (this.lottie) {
                this.lottie.resize();
            }
        },
        handleTrigger() {
            if(this.trigger == "load") {
                this.playAnimation();
            }
        },
        playAnimation(direction = null) {
            if(direction) {
                this.setDirection(direction);
            }
            this.lottie.play();
            this.$emit('playing', direction);
        },
        stopAnimation() {
            this.lottie.stop();
        },
        pauseAnimation() {
            this.lottie.pause();
        },
        setDirection(direction) {
            this.lottie.setDirection(direction);
        },
        goToAndStop(value, isFrame) {
            if (this.lottie && this._isLoaded) {
                this.lottie.goToAndStop(value, isFrame);
            } else {
                this._pendingSeekFrame = { value, isFrame };
            }
        },
        goToAndPlay(value, isFrame) {
            this.lottie.goToAndPlay(value, isFrame);
        },
        playSegments(segments, forceFlag) {
            this.lottie.playSegments(segments, forceFlag)
        },
        rewindAnimation() {
            this.lottie.stop();
        },
        emitComplete() {
            this.$emit('completed');
        },
        getTotalFrames() {
            return this.lottie ? this.lottie.totalFrames : 0;
        },
        getCurrentFrame() {
            return this.lottie.currentFrame;
        },
        renderFrame() {
            const renderer = this.lottie.renderer;

            if(renderer) {
                renderer.renderFrame(null);
            }
        }
    }
}
</script>
